import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Icon,
  Typography,
  Grid,
  Stack,
  Box,
  AccordionGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  AppHeaderBackButton,
} from '@ace-clublabs/shared-component-library';
import { TooltipModal, TooltipModalSubContent } from '../TooltipModal';
import { IconName } from '@ace-clublabs/shared-component-library/dist/components/Icons';
import { MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { getMembershipTitle, promotionMap } from '../../utils/stringHelper';
import { PillTag } from './PillTag';
import { getBenefitButtonText } from './MembershipBenefit';
import { useContext } from 'react';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { IBenefitsComparison, IBenefitsComparisonCellValue, IBenefitsComparisonRow } from '@lib/types';
import DOMPurify from 'dompurify';

const LEVEL_COLUMNS: MembershipLevel[] = [MembershipLevel.CLASSIC, MembershipLevel.PLUS, MembershipLevel.PREMIER];

interface BenefitItem {
  title: string;
  tooltipTitle?: string;
  subtitle?: string;
  tooltip?: string;
  valuePairs?: IBenefitsComparison['valuePairs'];
  value: { levels: MembershipLevel[]; value: IBenefitsComparisonCellValue }[];
}

interface BenefitCategory {
  label: string;
  iconName?: IconName;
  items: BenefitItem[];
}

const mapRowToValueGroups = (row: IBenefitsComparisonRow): BenefitItem['value'] => {
  const groups: BenefitItem['value'] = [];

  if (row.sharing) {
    const sharingLevels = row.sharing.level.map((l) => l.toUpperCase() as MembershipLevel);
    const sharingGroup = { levels: sharingLevels, value: row.sharing };
    const firstSharingIndex = Math.min(...sharingLevels.map((l) => LEVEL_COLUMNS.indexOf(l)));

    for (const key of LEVEL_COLUMNS) {
      if (key === LEVEL_COLUMNS[firstSharingIndex]) {
        groups.push(sharingGroup);
      } else if (!sharingLevels.includes(key)) {
        const cell = row[key as 'CLASSIC' | 'PLUS' | 'PREMIER'];
        if (cell) groups.push({ levels: [key], value: cell });
      }
    }
    return groups;
  }

  for (const key of LEVEL_COLUMNS) {
    const cell = row[key as 'CLASSIC' | 'PLUS' | 'PREMIER'];
    if (cell) groups.push({ levels: [key], value: cell });
  }
  return groups;
};

export const mapBenefitsComparisonData = (data: IBenefitsComparison[] | null | undefined): BenefitCategory[] =>
  (data || []).map((table) => ({
    label: table.name,
    iconName: (table.iconName as IconName) || undefined,
    items: [...table.rows]
      .sort((a, b) => Number(a.sequenceNo) - Number(b.sequenceNo))
      .map((row) => {
        const valuePairs = table.valuePairs;
        const tooltipContent = row.tooltipcontent?.length
          ? row.tooltipcontent.join('\n')
          : row.tooltiptitle || undefined;
        return {
          title: row.title,
          tooltipTitle: row.tooltiptitle ? processValuePairs(row.tooltiptitle, valuePairs) : undefined,
          subtitle: row.subtitle?.length ? row.subtitle.join('\n') : undefined,
          tooltip: tooltipContent ? processValuePairs(tooltipContent, valuePairs) : undefined,
          valuePairs: table.valuePairs,
          value: mapRowToValueGroups(row),
        };
      }),
  }));

const PLAIN_TEXT_CONFIG = { ALLOWED_TAGS: [], ALLOWED_ATTR: [] };
const LINK_CONFIG = {
  ALLOWED_TAGS: ['a'],
  ALLOWED_ATTR: ['href', 'rel', 'style'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):)/i,
};

// DOMPurify is stripping the target="_blank" attribute
// Add target="_blank" back to <a> tags after sanitization
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener');
  }
});

const processValuePairs = (text: string, valuePairs?: IBenefitsComparison['valuePairs']): string => {
  let result = DOMPurify.sanitize(text, PLAIN_TEXT_CONFIG);
  result = result.replaceAll('®', '<sup>®</sup>');
  if (valuePairs) {
    for (const pair of valuePairs) {
      if (!pair.name) continue;
      const formatValue = (value: string): string => {
        let formattedValue = DOMPurify.sanitize(value, PLAIN_TEXT_CONFIG);
        if (pair.value.isLink) {
          formattedValue = DOMPurify.sanitize(
            '<a href="' + pair.value.link + '" style="color: #0156A3">' + formattedValue + '</a>',
            LINK_CONFIG
          );
        }
        if (pair.value.isBold) {
          formattedValue = '<b>' + formattedValue + '</b>';
        }
        if (pair.value.isItalics) {
          formattedValue = '<i>' + formattedValue + '</i>';
        }
        if (pair.value.isSuperscript) {
          formattedValue = '<sup>' + formattedValue + '</sup>';
        }
        return formattedValue;
      };
      let replaceValue;
      if (pair.value.bulletList) {
        replaceValue =
          '<ul>' + pair.value.bulletList.map((value) => '<li>' + formatValue(value) + '</li>').join('') + '</ul>';
      } else {
        replaceValue = formatValue(pair.value.text ?? '');
      }
      if (pair.value.isLineBreak) {
        replaceValue += '<br />';
      }
      result = result.replaceAll('{{' + pair.name + '}}', replaceValue);
    }
  }
  return result;
};

const InfoButton: React.FC<{ item: BenefitItem; onInfoClick: (title: string, content: string) => void }> = ({
  item,
  onInfoClick,
}) => {
  if (!item.tooltip) return null;
  return (
    <IconButton
      size="small"
      aria-label={`More info about ${item.title}`}
      onClick={() => onInfoClick(item.tooltipTitle ?? '', item.tooltip!)}
      sx={{ width: '1.5rem', height: '1.5rem', minWidth: 0, padding: 0 }}
    >
      <Icon iconName="circle-info-regular" sx={{ color: '#707070', fontSize: '1.25rem' }} />
    </IconButton>
  );
};

const BenefitValueCell: React.FC<{
  cellValue: IBenefitsComparisonCellValue;
  valuePairs?: IBenefitsComparison['valuePairs'];
}> = ({ cellValue, valuePairs }) => {
  if (cellValue.formatingtype === 'check') {
    return <Icon iconName="check-regular" sx={{ color: '#3E6B20', fontSize: '1.5rem' }} />;
  }
  if (cellValue.formatingtype === 'x') {
    return <Icon iconName="xmark-regular" sx={{ color: '#A81421', fontSize: '1.5rem' }} />;
  }
  const processed = processValuePairs(cellValue.text ?? '', valuePairs);
  return (
    <Typography component="div" variant="b2Regular" sx={{ textAlign: 'center', whiteSpace: 'pre-line' }}>
      <div dangerouslySetInnerHTML={{ __html: processed }} />
    </Typography>
  );
};

const TitleCell: React.FC<{
  item: BenefitItem;
  onInfoClick: (title: string, content: string) => void;
}> = ({ item, onInfoClick }) => (
  <Grid size={4} sx={{ backgroundColor: '#F8F8FA', padding: '1rem', alignSelf: 'stretch' }}>
    <Stack direction="row" alignItems="flex-start" gap="0.25rem" justifyContent="space-between">
      <Stack>
        <Typography component="div" variant="b2Medium">
          <div dangerouslySetInnerHTML={{ __html: processValuePairs(item.title, item.valuePairs) }} />
        </Typography>
        {item.subtitle && (
          <Typography component="div" variant="b3Regular" sx={{ color: '#707070', whiteSpace: 'pre-line' }}>
            <div dangerouslySetInnerHTML={{ __html: processValuePairs(item.subtitle, item.valuePairs) }} />
          </Typography>
        )}
      </Stack>
      <InfoButton item={item} onInfoClick={onInfoClick} />
    </Stack>
  </Grid>
);

const COLUMN_SIZE = 8 / 3;

const ValueCell: React.FC<{
  cellValue: IBenefitsComparisonCellValue;
  valuePairs?: IBenefitsComparison['valuePairs'];
  size?: number;
  sx?: object;
}> = ({ cellValue, valuePairs, size = COLUMN_SIZE, sx }) => (
  <Grid
    size={size}
    sx={{ padding: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, ...sx }}
  >
    <BenefitValueCell cellValue={cellValue} valuePairs={valuePairs} />
  </Grid>
);

const BenefitRow: React.FC<{
  item: BenefitItem;
  onInfoClick: (title: string, content: string) => void;
  mobileView?: boolean;
}> = ({ item, onInfoClick, mobileView }) => {
  const hasIcon = item.value.some((g) => g.value.formatingtype === 'check' || g.value.formatingtype === 'x');

  if (mobileView) {
    return (
      <>
        <Divider sx={{ borderColor: '#E9EDF5' }} />
        <Box sx={{ backgroundColor: '#F8F8FA', padding: '1rem' }}>
          <Stack direction="row" alignItems="flex-start" gap="0.25rem" justifyContent="space-between">
            <Stack>
              <Typography component="div" variant="b1Medium">
                <div dangerouslySetInnerHTML={{ __html: processValuePairs(item.title, item.valuePairs) }} />
              </Typography>
              {item.subtitle && (
                <Typography component="div" variant="b3Regular" sx={{ color: '#707070', whiteSpace: 'pre-line' }}>
                  <div dangerouslySetInnerHTML={{ __html: processValuePairs(item.subtitle, item.valuePairs) }} />
                </Typography>
              )}
            </Stack>
            <InfoButton item={item} onInfoClick={onInfoClick} />
          </Stack>
        </Box>
        <Grid container alignItems={hasIcon ? 'center' : 'flex-start'} sx={{ gap: 0, flexWrap: 'nowrap' }}>
          {item.value.map((group, index) => (
            <ValueCell
              key={index}
              cellValue={group.value}
              valuePairs={item.valuePairs}
              size={(12 / 3) * group.levels.length}
            />
          ))}
        </Grid>
      </>
    );
  }

  return (
    <>
      <Divider sx={{ borderColor: '#E9EDF5' }} />
      <Grid container alignItems={hasIcon ? 'center' : 'flex-start'} sx={{ gap: 0, flexWrap: 'nowrap' }}>
        <TitleCell item={item} onInfoClick={onInfoClick} />
        {item.value.map((group, index) => (
          <ValueCell
            key={index}
            cellValue={group.value}
            valuePairs={item.valuePairs}
            size={COLUMN_SIZE * group.levels.length}
          />
        ))}
      </Grid>
    </>
  );
};

const supStyle = { fontSize: '0.8em', verticalAlign: 'text-bottom' } as const;

const getHeaderTitle = (level: MembershipLevel): React.ReactNode => {
  const title = getMembershipTitle(level);
  const isClassic = level === MembershipLevel.CLASSIC;
  return (
    <span>
      {title}
      <sup style={{ ...supStyle, ...(isClassic && { visibility: 'hidden', opacity: 0 }) }}>®</sup>
    </span>
  );
};

interface BenefitsTableProps {
  allowedMembershipLevels: MembershipLevel[];
  isUpgrading?: boolean;
  previousMembershipLevel?: MembershipLevel;
  onLevelSelect?: (level: MembershipLevel) => void;
  onClose?: () => void;
  mobileView?: boolean;
}

export const BenefitsTable: React.FC<BenefitsTableProps> = ({
  allowedMembershipLevels,
  isUpgrading,
  previousMembershipLevel,
  onLevelSelect,
  onClose,
  mobileView,
}) => {
  const { sessionData } = useContext(MembershipAppStateContext);
  const [tooltipInfo, setTooltipInfo] = useState<{ title: string; content: string } | null>(null);
  const benefitData = mapBenefitsComparisonData(sessionData.membershipContent?.benefitsComparison);

  const handleInfoClick = (title: string, content: string) => {
    setTooltipInfo({ title, content });
  };

  if (mobileView === false) return null;

  const containerSx = mobileView
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        backgroundColor: '#fff',
        overflow: 'auto',
      }
    : {};

  return (
    <Box className="benefits-table-container" sx={containerSx}>
      {mobileView ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" padding="1rem">
          <AppHeaderBackButton onClick={onClose} />
          <Typography id="benefits-table-header" variant="b1Medium">
            See all membership benefits
          </Typography>
        </Stack>
      ) : (
        <Typography
          id="benefits-table-header"
          variant="h4"
          marginTop="2rem"
          marginBottom="1.5rem"
          alignSelf="center"
          sx={{ textAlign: 'center', scrollMarginTop: '1rem' }}
        >
          See all membership benefits
        </Typography>
      )}
      <Grid
        container
        className="benefit-compare-level-header"
        sx={{
          backgroundColor: '#E9EDF5',
          borderRadius: '0.5rem 0.5rem 0 0',
          borderBottom: '1px solid var(--Navy-200, #CED9E9)',
          gap: 0,
          flexWrap: 'nowrap',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {!mobileView && <Grid size={4} />}
        {allowedMembershipLevels.map((level, index) => {
          const pill = promotionMap[level];
          const isLast = index === allowedMembershipLevels.length - 1;
          return (
            <Grid
              key={level}
              size={mobileView ? 12 / 3 : COLUMN_SIZE}
              sx={{ ...(!isLast && { borderRight: '1px solid #CED9E9' }), flexGrow: 1 }}
              className={`benefit-level-${level}`}
            >
              <Stack direction="column" gap="0.75rem" alignItems="center" padding="1rem">
                <Stack
                  direction="row"
                  gap="0.5rem"
                  alignItems="center"
                  justifyContent={mobileView ? 'center' : 'space-between'}
                  width="100%"
                >
                  <Typography variant="h6" className={`benefit-level-title-${level}`}>
                    {getHeaderTitle(level)}
                  </Typography>
                  <PillTag
                    content={pill.shortText}
                    color={pill.color}
                    className={`benefit-level-tag-${level}`}
                    displayMethod="desktopOnly"
                  />
                </Stack>
                <Button
                  label={getBenefitButtonText(
                    level,
                    Boolean(isUpgrading),
                    previousMembershipLevel ?? MembershipLevel.CLASSIC,
                    mobileView
                  )}
                  fullWidth
                  variant="contained"
                  size="medium"
                  onClick={() => onLevelSelect?.(level)}
                  className={`benefit-level-button-${level}`}
                  sx={{ padding: '8px 16px !important', whiteSpace: 'nowrap' }}
                />
              </Stack>
            </Grid>
          );
        })}
      </Grid>
      <AccordionGroup
        sx={{ maxWidth: '100%', border: 'none', borderRadius: 0, borderBottom: '1px solid var(--Navy-200, #CED9E9)' }}
        className={`benefits-table-details`}
      >
        {benefitData.map((category, index) => (
          <Accordion
            key={category.label}
            defaultExpanded={index === 0}
            sx={{ backgroundColor: '#fff', maxWidth: '100%', border: 'none', '&::before': { display: 'none' } }}
            className={`benefits-table-${index}`}
          >
            <AccordionSummary
              expandIcon={<Icon iconName="chevron-down-regular" />}
              collapseIcon={<Icon iconName="chevron-up-regular" />}
              sx={{ backgroundColor: '#E9EDF5', borderBottom: '1px solid var(--Navy-200, #CED9E9)' }}
            >
              <Stack direction="row" alignItems="center" gap="0.5rem" className={`benefits-accordion-${category}`}>
                {category.iconName && (
                  <Icon iconName={category.iconName} sx={{ fontSize: '1.75rem', color: '#3F5F90' }} />
                )}
                <Typography variant="b1Bold" sx={{ color: '#3D4043 !important' }}>
                  {category.label}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {category.items
                .filter((item) => !item.value.every((group) => group.value.formatingtype === 'x'))
                .map((item) => (
                  <BenefitRow key={item.title} item={item} onInfoClick={handleInfoClick} mobileView={mobileView} />
                ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionGroup>

      <TooltipModal
        tooltipTitle={tooltipInfo?.title ?? ''}
        showModal={!!tooltipInfo}
        closeModal={() => setTooltipInfo(null)}
      >
        <TooltipModalSubContent tooltipContent={tooltipInfo?.content ?? ''} />
      </TooltipModal>
    </Box>
  );
};
